import { Injectable } from '@angular/core';
import { BullhornDataSourceApiService } from 'datasource-service-lib';
import { IEntityMetadata, IEntityMetadataService } from 'interfaces-lib';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap, toArray } from 'rxjs/operators';
import { BullhornSettingsService } from 'settings-lib';

@Injectable({ providedIn: 'root' })
export class BullhornEntityMetadataService implements IEntityMetadataService {
  private _cache: IEntityMetadata[] | null = null;

  // NOTE: We only need the Entity Label, we are not using the entity fields meta
  private entities = [
    { name: 'Placement', fields: ['id'] },
    { name: 'Candidate', fields: ['id'] },
    { name: 'JobOrder', fields: ['id'] },
    { name: 'ClientContact', fields: ['id'] },
    { name: 'ClientCorporation', fields: ['id'] },
    { name: 'JobSubmission', fields: ['id'] },
    { name: 'Lead', fields: ['id'] },
    { name: 'Opportunity', fields: ['id'] },
  ];

  constructor(private apiService: BullhornDataSourceApiService, private bullhornSettingsService: BullhornSettingsService) {}

  getEntitiesMetadata(): Observable<IEntityMetadata[]> {
    if (this._cache) {
      return of(this._cache);
    }

    return this.fetchEntitiesMetadata().pipe(tap((data) => (this._cache = data))) as Observable<IEntityMetadata[]>;
  }

  private fetchEntitiesMetadata(): Observable<IEntityMetadata[]> {
    return this.bullhornSettingsService.clientLeadOpportunityEnabled.pipe(
      map((enabled) => this.filterLeadsAndOpportunities(enabled)),
      mergeMap((entities) => from(entities)),
      mergeMap((entity) => this.fetchEntityMetadata(entity), this.entities.length),
      toArray(),
      map((data) => this.processMetadata(data))
    );
  }

  private filterLeadsAndOpportunities(enabled: boolean) {
    if (!enabled) {
      return this.entities.filter((entity) => entity.name !== 'Lead' && entity.name !== 'Opportunity');
    }
    return this.entities;
  }

  private fetchEntityMetadata(entity: {name: string, fields: string[]}): Observable<IEntityMetadata> {
    // console.log('get entity meta for: ', entity);
    return this.apiService.meta({ entity: entity.name, fields: entity.fields }).pipe(
      catchError((error) => {
        console.error(`Failed to load metadata for entity ${entity}`, error);
        throw error;
      })
    );
  }

  private processMetadata(data: IEntityMetadata[]): IEntityMetadata[] {
    const result: IEntityMetadata[] = [];
    data.forEach((meta) => {
      if (meta) {
        result.push(meta);
      }
    });
    return result;
  }
}
